/** @jsxImportSource @emotion/react */
import { Countdown } from "./Countdown"
import { useTranslation } from "react-i18next"
import { css, Theme } from "@mui/material"
import { withMyTheme } from "../../theme/theme"
import { mobileCss } from "../../theme/isMobile"
import { tabletCss } from "../../theme/isTablet"
import { MyButton } from "../../components/button/MyButton"
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import { Fullscreen } from "../../components/Fullscreen"
import { openUrl } from "../../util/openUrl";
import { getMarriageDateTime, getMarriageDateString, MainConfig, getMarriageTimeString } from "../../../mainConfig/configHelper"
import { OldConfig } from "../../../config/OldConfig"

export const TITLE_ID = "title"

const TitleContainerStyle = withMyTheme((theme: Theme) => css`
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    font-family: ${theme.typography.body1.fontFamily};
    position: relative;
    overflow: hidden;
    z-index: 1;

    ${mobileCss(`
        justify-content: center;
    `)}
`)

const BackgroundImageStyle = withMyTheme(() => css`
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    scale: 1.5;
    translate: 0.5% 0;
    height: 100vh;
    background-color: #000;
    z-index: 1;
`)

const OverlayStyle = withMyTheme(() => css`
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    opacity: 60%;
    background-color: #000;
    z-index: 2;
`)

const ContentWrapperStyle = withMyTheme((theme: Theme) => css`
    z-index: 3;
    color: #fff;
    font-weight: 200;
    display: flex;
    height: 100vh;
    align-content: center;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    justify-items: center;
    padding: 2rem 1rem;
    width: 90%;

    ${tabletCss(`
        padding: 1.75rem 1rem;
        width: 95%;
    `)}
    ${mobileCss(`
        padding: 1.5rem 0.75rem;
        width: 95%;
        align-items: center;
        justify-content: flex-start;
    `)}
`)

const TitleTextStyle = withMyTheme((theme: Theme) => css`
    font-size: 6.3vw;
    color: ${theme.palette.primary.main};
    font-family: ${theme.typography.h1.fontFamily};
    font-weight: 500;
    max-lines: 1;

    ${mobileCss(`
        font-size: 3.5em;
        margin-top: 3.5vh;
    `)}
`)

const DateHourWrapperStyle = withMyTheme((theme) => css`
    line-height: 1.5;
    cursor: pointer;
    font-family: ${theme.typography.h1.fontFamily};
`)

const DateStyle = withMyTheme((theme) => css`
    font-size: 4.3vw;
    color: ${theme.palette.primary.contrastText};
    font-family: ${theme.typography.h1.fontFamily};
    
    ${mobileCss(`
        font-size: 2em;
        cursor: pointer;
    `)}
`)

const HourStyle = withMyTheme(() => css`
    font-size: 3.2vw;
    color: #fff;
    margin-bottom: 1em;

    ${mobileCss(`
        font-size: 1.5em;
        cursor: pointer;
    `)}
`)

const CalendarButtonWrapperStyle = withMyTheme(() => css`
    margin-top: .6em;

    ${mobileCss(`
        margin-top: auto;
    `)}
`)

const CalendarButtonStyle = withMyTheme(() => css`
    font-size: 2vh;

    ${mobileCss(`
        font-size: 3vw;
    `)}
`)

const CountdownWrapperStyle = withMyTheme(() => css`
    margin-top: 2em;
    margin-bottom: 2em;
`)

export const Title = () => {
    const { t } = useTranslation()

    const addEventToGoogleCalendar = () => {
        const endTime = getMarriageDateTime()
        endTime.setDate(getMarriageDateTime().getDate() + 1);
        endTime.setHours(4);
        const event = {
            title: MainConfig.basicInfo.coupleName1 + ` ${t('title.and')} ` + MainConfig.basicInfo.coupleName2 + " " + t('title.calendarEventName'),
            description: MainConfig.ceremonyLocation.details + "\n\n" + MainConfig.weddingLocation.details,
            location: MainConfig.ceremonyLocation.location,
            startTime: getMarriageDateTime().toISOString(),
            endTime: endTime.toISOString(),
        };

        const googleCalendarUrl = `https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(event.title)}&details=${encodeURIComponent(event.description)}&location=${encodeURIComponent(event.location)}&dates=${event.startTime.replace(/-|:|\./g, '')}/${event.endTime.replace(/-|:|\./g, '')}`;

        openUrl(googleCalendarUrl, false)
    };

    const imagePath = MainConfig.mainImages.desktopImage

    return (
        <Fullscreen id={TITLE_ID} additionalCss={TitleContainerStyle}>
            {/* Invisible observer for FloatingButtons */}
            <div
                id="title-observer"
                style={{
                    position: 'absolute',
                    top: '50vh',
                    left: 0,
                    width: '1px',
                    height: '1px',
                    pointerEvents: 'none',
                    zIndex: -1
                }}
            />

            <img css={BackgroundImageStyle} src={imagePath} alt="Wedding background" />
            <div css={OverlayStyle} />
            <div css={ContentWrapperStyle}>
                <div css={TitleTextStyle}>
                    {MainConfig.basicInfo.coupleName1} {t('title.and')} {MainConfig.basicInfo.coupleName2}
                </div>
                <div css={DateHourWrapperStyle} onClick={addEventToGoogleCalendar}>
                    <span css={DateStyle}>
                        {getMarriageDateString()}
                    </span>
                    <br />
                    <span css={HourStyle}>
                        {getMarriageTimeString()}
                    </span>
                </div>
                <div css={CalendarButtonWrapperStyle}>
                    <MyButton
                        additionalCss={CalendarButtonStyle}
                        variant="outlined"
                        colorVariant="secondary"
                        startIcon={<CalendarMonthIcon fontSize="inherit" />}
                        onClick={addEventToGoogleCalendar}
                        text={t('title.addEventToCalendar')}
                    />
                </div>
                <div css={CountdownWrapperStyle}>
                    <Countdown targetDate={getMarriageDateTime()} addEventToGoogleCalendar={addEventToGoogleCalendar} />
                </div>
            </div>
        </Fullscreen>
    )
}
